import { ApplicationConfig, ErrorHandler, provideZoneChangeDetection, APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { environment } from '../environments/environment';
import { provideRouter, withDebugTracing } from '@angular/router';
import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import Aura from '@primeuix/themes/aura';
import { authInterceptor } from './token/token.interceptor';
import { httpErrorReportingInterceptor } from './token/http-error-reporting.interceptor';
import { httpTimeoutInterceptor } from './token/http-timeout.interceptor';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { providePrimeNG } from 'primeng/config';
import { UserSettingsService } from './_service/user-settings.service';
import { GlobalErrorHandler } from './_service/global-error-handler.service';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';


// Hard cap on app bootstrap, independent of the httpTimeoutInterceptor: Angular does not
// render <app-root> until every APP_INITIALIZER resolves, so if UserSettingsService.initialize()
// were ever to hang on something other than an HTTP call (e.g. a browser/IndexedDB quirk),
// the whole app would show a blank page indefinitely. This guarantees bootstrap always
// proceeds within a bounded time, offline or not.
const APP_INITIALIZER_HARD_TIMEOUT_MS = 25_000;

export function initUserSettings(settingsService: UserSettingsService) {
  return () => {
    console.log('[APP_INITIALIZER] Running UserSettingsService.initialize()');
    return Promise.race([
      settingsService.initialize(),
      new Promise<void>((resolve) =>
        setTimeout(() => {
          console.warn('[APP_INITIALIZER] UserSettingsService.initialize() exceeded hard timeout; continuing bootstrap anyway.');
          resolve();
        }, APP_INITIALIZER_HARD_TIMEOUT_MS),
      ),
    ]);
  };
}


export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimationsAsync(),
    {
      provide: APP_INITIALIZER,
      useFactory: initUserSettings,
      deps: [UserSettingsService],
      multi: true
    },
    providePrimeNG({
      theme: {
        preset: Aura,
        options: { darkModeSelector: '.p-dark' },
      },
    }),
    importProvidersFrom(ToastModule),
    MessageService,
    ConfirmationService,
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes,withDebugTracing()),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideHttpClient(
      withInterceptors([authInterceptor, httpErrorReportingInterceptor, httpTimeoutInterceptor]),
    ),
    provideServiceWorker('ngsw-worker.js', {
      // Was `!isDevMode()`: isDevMode() reflects whether the build stripped
      // ngDevMode, which the deploy script's fast/no-optimization path
      // (`ng build --configuration=production --optimization=false`) leaves
      // true even in a real production deploy — so the service worker (and
      // "Check for Updates") looked permanently disabled after every fast
      // deploy. environment.production is set via a proper fileReplacement
      // on the "production" build configuration, so it's correct regardless
      // of the optimization flag.
      enabled: environment.production,
      registrationStrategy: 'registerImmediately'
    }),


  ],
};
